import { useEffect, useRef, useState } from "react";
import { Camera, Upload, RotateCw, RotateCcw, Star, Trash2, AlertTriangle, Loader2, ImagePlus, Crop } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import CropModal from "@/components/admin/CropModal";
import { GoldAction } from "@/components/admin/GoldUi";

export default function ImageManager({ images, setImages, locationId, onBusyChange }) {
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [drag, setDrag] = useState(false);
  const [cropImg, setCropImg] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  useEffect(() => { onBusyChange?.(uploading || !!busyId || !!cropImg); }, [uploading, busyId, cropImg, onBusyChange]);

  const fmtSize = (b) => (!b ? "" : b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  const handleFiles = async (fileList) => {
    if (uploading || busyId || cropImg) return;
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (locationId) fd.append("location_id", locationId);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => {
          const isFirst = prev.length === 0;
          const next = [...prev, { ...data, is_main: isFirst }];
          return next;
        });
        if (data.original_bytes && data.processed_bytes) {
          const saved = Math.max(0, Math.round((1 - data.processed_bytes / data.original_bytes) * 100));
          toast.success(`Optimiert und gespeichert · Original ${fmtSize(data.original_bytes)} → FUNDIO ${fmtSize(data.processed_bytes)} · −${saved}%`);
        } else {
          toast.success("Optimiert und gespeichert");
        }
        if (data.quality?.warnings?.length) {
          toast.warning(data.quality.warnings[0]);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Bild konnte nicht hochgeladen werden.");
      }
    }
    setUploading(false);
  };

  const rotate = async (img, degrees) => {
    if (uploading || busyId || cropImg) return;
    setBusyId(img.id);
    try {
      const { data } = await api.post(`/images/${img.id}/rotate`, { degrees });
      setImages((prev) => prev.map((x) => (x.id === img.id ? { ...x, ...data } : x)));
    } catch {
      toast.error("Drehen fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const setMain = (img) => setImages((prev) => prev.map((x) => ({ ...x, is_main: x.id === img.id })));

  const remove = (img) => setImages((prev) => {
    const next = prev.filter((x) => x.id !== img.id);
    if (img.is_main && next.length) next[0].is_main = true;
    return [...next];
  });

  return (
    <div>
      {/* drop zone */}
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        className={`fundio-upload-zone ${drag ? "fundio-upload-zone--drag" : ""}`}
      >
        <ImagePlus size={36} aria-hidden="true" />
        <p>Fotos auswählen oder aufnehmen</p>
        <p className="text-sm fundio-muted">Du kannst Bilder auch hierher ziehen.</p>
        <div className="fundio-upload-actions">
          <GoldAction variant="primary" full data-testid="camera-btn" disabled={uploading || !!busyId || !!cropImg} onClick={() => camRef.current?.click()}><Camera size={20} aria-hidden="true" /> Foto aufnehmen</GoldAction>
          <GoldAction full data-testid="upload-btn" disabled={uploading || !!busyId || !!cropImg} onClick={() => fileRef.current?.click()}><Upload size={20} aria-hidden="true" /> Bilder hochladen</GoldAction>
        </div>
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => handleFiles(e.target.files)} />
        <input ref={fileRef} type="file" accept="image/*,.heic,.heif" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
        {uploading && <p role="status" className="mt-3 flex items-center justify-center gap-2 text-sm fundio-muted" data-testid="upload-optimizing"><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Bild wird optimiert …</p>}
      </div>

      {/* grid */}
      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="image-grid">
          {images.map((img) => (
            <div key={img.id} className="group relative overflow-hidden rounded-lg border border-fundio-border bg-white">
              <div className="relative aspect-square">
                <img src={fileUrl(img.thumb_url || img.thumb_path)} alt="" className="h-full w-full object-cover" />
                {busyId === img.id && <div className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 className="animate-spin text-fundio-gold" /></div>}
                {img.is_main && <span className="absolute left-1.5 top-1.5 rounded bg-fundio-gold px-2 py-0.5 text-[10px] font-bold text-fundio-black">Hauptbild</span>}
                {img.quality?.warnings?.length > 0 && (
                  <span title={img.quality.warnings.join(" ")} className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500/90 text-white"><AlertTriangle size={13} /></span>
                )}
              </div>
              <div className="fundio-image-actions border-t border-fundio-border">
                <div className="flex gap-1">
                  <button type="button" title="Links drehen" onClick={() => rotate(img, -90)} className="rounded p-1.5 text-fundio-slate hover:bg-fundio-subtle hover:text-fundio-ink"><RotateCcw size={15} /></button>
                  <button type="button" title="Rechts drehen" onClick={() => rotate(img, 90)} className="rounded p-1.5 text-fundio-slate hover:bg-fundio-subtle hover:text-fundio-ink"><RotateCw size={15} /></button>
                  <button type="button" title="Zuschneiden" data-testid={`crop-btn-${img.id}`} onClick={() => setCropImg(img)} className="rounded p-1.5 text-fundio-slate hover:bg-fundio-subtle hover:text-fundio-gold"><Crop size={15} /></button>
                </div>
                <div className="flex gap-1">
                  {!img.is_main && <button type="button" title="Als Hauptbild" onClick={() => setMain(img)} className="rounded p-1.5 text-fundio-slate hover:bg-fundio-subtle hover:text-fundio-gold"><Star size={15} /></button>}
                  <button type="button" title="Entfernen" onClick={() => remove(img)} className="rounded p-1.5 text-fundio-slate hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {cropImg && (
        <CropModal
          image={cropImg}
          onClose={() => setCropImg(null)}
          onCropped={(data) => setImages((prev) => prev.map((x) => (x.id === cropImg.id ? { ...x, ...data } : x)))}
        />
      )}
    </div>
  );
}
